import React, { useEffect, useRef } from 'react';

function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

const O = '#FF7A1A';
const O3 = 'rgba(255,122,26,';

export default function SecurityCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    let animId = 0, t = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const W = () => canvas.offsetWidth;
    const H = () => canvas.offsetHeight;

    // Particles
    const particles = Array.from({ length: 55 }, () => ({
      x: Math.random() * 1200,
      y: Math.random() * 700,
      vx: (Math.random() - 0.5) * 0.28,
      vy: (Math.random() - 0.5) * 0.28,
      r: 0.4 + Math.random() * 1.6,
      o: Math.random() * 0.5 + 0.1,
      od: Math.random() > 0.5 ? 1 : -1,
    }));

    const orbiters = Array.from({ length: 10 }, (_, i) => ({
      angle: (Math.PI * 2 / 10) * i,
      r: 90 + Math.floor(i / 4) * 28,
      spd: 0.006 + (i % 3) * 0.003,
      size: 2 + Math.random()
    }));

    const mkServers = () => {
      const w = W(), h = H();
      return [
        { x: w * 0.08, y: h * 0.42, label: 'SRV-01' },
        { x: w * 0.92, y: h * 0.38, label: 'SRV-02' },
        { x: w * 0.25, y: h * 0.18, label: 'SRV-03' },
        { x: w * 0.75, y: h * 0.72, label: 'SRV-04' },
      ];
    };
    let servers = mkServers();

    const buildArcs = () =>
      servers.map(s => ({ from: s, prog: Math.random(), spd: 0.003 + Math.random() * 0.003 }));
    let arcs = buildArcs();

    const drawGrid = (w, h) => {
      ctx.strokeStyle = O3 + '0.055)';
      ctx.lineWidth = 0.5;
      const sz = 40;
      for (let x = 0; x < w; x += sz) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
      for (let y = 0; y < h; y += sz) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
    };

    function shieldPath(ctx, sc) {
      ctx.beginPath();
      ctx.moveTo(0, -sc);
      ctx.bezierCurveTo(sc * 0.7, -sc * 0.9, sc, -sc * 0.5, sc, -sc * 0.1);
      ctx.bezierCurveTo(sc, sc * 0.4, sc * 0.5, sc * 0.8, 0, sc * 1.05);
      ctx.bezierCurveTo(-sc * 0.5, sc * 0.8, -sc, sc * 0.4, -sc, -sc * 0.1);
      ctx.bezierCurveTo(-sc, -sc * 0.5, -sc * 0.7, -sc * 0.9, 0, -sc);
      ctx.closePath();
    }

    const drawShield = (cx, cy) => {
      const sc = 78;
      const phase = (Math.sin(t * 0.7) + 1) / 2;

      // Glow rings
      for (let i = 3; i >= 0; i--) {
        const extra = i * 10 + phase * 5;
        ctx.save(); ctx.translate(cx, cy); ctx.scale((sc + extra) / sc, (sc + extra) / sc);
        shieldPath(ctx, sc); ctx.restore();
        ctx.fillStyle = O3 + (0.04 - i * 0.006) + ')'; ctx.fill();
      }

      // Body
      ctx.save(); ctx.translate(cx, cy); shieldPath(ctx, sc); ctx.restore();
      const bg = ctx.createLinearGradient(cx - sc, cy - sc, cx + sc, cy + sc);
      bg.addColorStop(0, 'rgba(40,18,0,0.75)'); bg.addColorStop(1, 'rgba(18,8,0,0.75)');
      ctx.fillStyle = bg; ctx.fill();
      ctx.shadowColor = O; ctx.shadowBlur = 28 + phase * 12;
      ctx.save(); ctx.translate(cx, cy); shieldPath(ctx, sc); ctx.restore();
      ctx.strokeStyle = O3 + (0.75 + phase * 0.25) + ')'; ctx.lineWidth = 2; ctx.stroke();
      ctx.shadowBlur = 0;

      // Inner ring
      ctx.save(); ctx.translate(cx, cy); ctx.scale(0.55, 0.55); shieldPath(ctx, sc); ctx.restore();
      ctx.strokeStyle = O3 + '0.18)'; ctx.lineWidth = 1; ctx.stroke();

      // Lock inside
      const lx = cx, ly = cy + 4;
      ctx.shadowColor = O; ctx.shadowBlur = 12;
      rr(ctx, lx - 13, ly - 2, 26, 20, 4);
      ctx.fillStyle = O3 + '0.18)'; ctx.fill();
      ctx.strokeStyle = O3 + '0.7)'; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.beginPath(); ctx.arc(lx, ly - 6, 9, Math.PI, 0);
      ctx.strokeStyle = O3 + '0.8)'; ctx.lineWidth = 2; ctx.stroke();
      ctx.beginPath(); ctx.arc(lx, ly + 6, 4, 0, Math.PI * 2);
      ctx.fillStyle = O; ctx.fill();
      ctx.shadowBlur = 0;
    };

    const drawServer = (s) => {
      const sw = 22, sh = 14;
      rr(ctx, s.x - sw, s.y - sh, sw * 2, sh * 2, 4);
      ctx.fillStyle = 'rgba(26,11,0,0.7)'; ctx.fill();
      ctx.shadowColor = O; ctx.shadowBlur = 10;
      ctx.strokeStyle = O3 + '0.6)'; ctx.lineWidth = 1; ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.fillStyle = O3 + '0.6)'; ctx.font = '500 7px Inter,sans-serif';
      ctx.textAlign = 'center'; ctx.fillText(s.label, s.x, s.y + sh + 10); ctx.textAlign = 'left';
      // LED
      ctx.beginPath(); ctx.arc(s.x + sw - 6, s.y, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = O; ctx.shadowColor = O; ctx.shadowBlur = 8; ctx.fill();
      ctx.shadowBlur = 0;
    };

    const drawArc = (arc, cx, cy) => {
      const { from: f, prog: p } = arc;
      const mx = (f.x + cx) / 2, my = Math.min(f.y, cy) - 55;
      const bx = (1 - p) * (1 - p) * f.x + 2 * (1 - p) * p * mx + p * p * cx;
      const by = (1 - p) * (1 - p) * f.y + 2 * (1 - p) * p * my + p * p * cy;

      // Trail
      ctx.beginPath(); ctx.moveTo(f.x, f.y);
      for (let i = 0; i <= p; i += 0.015) {
        const ix = (1 - i) * (1 - i) * f.x + 2 * (1 - i) * i * mx + i * i * cx;
        const iy = (1 - i) * (1 - i) * f.y + 2 * (1 - i) * i * my + i * i * cy;
        ctx.lineTo(ix, iy);
      }
      ctx.strokeStyle = O3 + (p * 0.35) + ')'; ctx.lineWidth = 1; ctx.stroke();

      ctx.shadowColor = O; ctx.shadowBlur = 10;
      ctx.beginPath(); ctx.arc(bx, by, 3, 0, Math.PI * 2);
      ctx.fillStyle = O; ctx.fill(); ctx.shadowBlur = 0;
    };

    const drawBuildings = (w, h) => {
      const bs = [
        { x: w * 0.04, bw: 28, bh: 55 }, { x: w * 0.09, bw: 22, bh: 72 }, { x: w * 0.15, bw: 32, bh: 45 },
        { x: w * 0.77, bw: 28, bh: 60 }, { x: w * 0.83, bw: 24, bh: 78 }, { x: w * 0.89, bw: 30, bh: 48 }, { x: w * 0.94, bw: 20, bh: 65 },
      ];
      bs.forEach(b => {
        const by = h - b.bh - 18;
        rr(ctx, b.x, by, b.bw, b.bh, 2);
        const g = ctx.createLinearGradient(b.x, by, b.x + b.bw, by + b.bh);
        g.addColorStop(0, 'rgba(28,12,0,0.5)'); g.addColorStop(1, 'rgba(14,6,0,0.5)');
        ctx.fillStyle = g; ctx.fill();
        ctx.strokeStyle = O3 + '0.2)'; ctx.lineWidth = 0.5; ctx.stroke();
        const cols = Math.floor(b.bw / 8), rows = Math.floor(b.bh / 10);
        for (let c = 0; c < cols; c++) for (let r = 0; r < rows; r++) {
          if (Math.random() > 0.4) {
            const wx = b.x + 4 + c * 8, wy = by + 5 + r * 10;
            const lit = Math.sin(t * 0.4 + c * 1.5 + r * 2.5) > 0.2;
            ctx.fillStyle = lit ? O3 + '0.45)' : O3 + '0.1)';
            ctx.fillRect(wx, wy, 4, 5);
          }
        }
      });
    };

    const draw = () => {
      t += 0.011;
      const w = W(), h = H();
      ctx.clearRect(0, 0, w, h);

      const ag = ctx.createRadialGradient(w * 0.5, h * 0.5, 0, w * 0.5, h * 0.5, w * 0.55);
      ag.addColorStop(0, 'rgba(60,25,0,0.1)'); ag.addColorStop(1, 'rgba(11,11,12,0)');
      ctx.fillStyle = ag; ctx.fillRect(0, 0, w, h);

      drawGrid(w, h);

      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.o += p.od * 0.005;
        if (p.o >= 0.6 || p.o <= 0.05) p.od *= -1;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0; if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = O3 + (p.o * 0.45) + ')'; ctx.fill();
      });

      drawBuildings(w, h);
      servers.forEach(s => drawServer(s));
      arcs.forEach(a => {
        a.prog += a.spd; if (a.prog > 1) a.prog = 0;
        drawArc(a, w * 0.5, h * 0.46);
      });

      // Orbiters
      orbiters.forEach(o => {
        o.angle += o.spd;
        const ox = w * 0.5 + Math.cos(o.angle) * o.r;
        const oy = h * 0.46 + Math.sin(o.angle) * o.r;
        ctx.shadowColor = O; ctx.shadowBlur = 8;
        ctx.beginPath(); ctx.arc(ox, oy, o.size, 0, Math.PI * 2);
        ctx.fillStyle = O3 + '0.8)'; ctx.fill(); ctx.shadowBlur = 0;
      });

      // Orbit rings
      [90, 118, 146].forEach((rad) => {
        ctx.beginPath(); ctx.arc(w * 0.5, h * 0.46, rad, 0, Math.PI * 2);
        ctx.strokeStyle = O3 + '0.07)'; ctx.lineWidth = 1; ctx.setLineDash([4, 8]); ctx.stroke();
        ctx.setLineDash([]);
      });

      drawShield(w * 0.5, h * 0.46);

      const sy = ((t * 38) % (h + 80)) - 40;
      const sg = ctx.createLinearGradient(0, sy - 2, 0, sy + 2);
      sg.addColorStop(0, 'transparent'); sg.addColorStop(0.5, O3 + '0.04)'); sg.addColorStop(1, 'transparent');
      ctx.fillStyle = sg; ctx.fillRect(0, sy - 2, w, 4);

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => { cancelAnimationFrame(animId); ro.disconnect(); };
  }, []);

  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}
